/**
 * Filing a trade.
 *
 * The form is two columns because a trade is two columns, and each column is a list of TYPED
 * assets rather than a text box. The text box is what the league has now — eleven years of
 * "Bijan + 2026 3rd for ₣12 (John may decline the 6th swap)" in a Discord channel — and it is
 * why nobody can answer whose ninth-rounder that is without scrolling.
 *
 * Four things here are not obvious and all four are deliberate:
 *
 * 1. A GM'S OWN SIDE IS LOCKED TO HIMSELF. He chooses the counterparty and nothing else about
 *    who is in it. A three-cornered deal is not a thing the rulebook has, and a form that let a
 *    man file a trade between two other people would need an affirmation from each of them.
 *
 * 2. "OTHER" IS NOT AN ASSET. It is stored as a clause, verbatim, and never becomes something
 *    anybody owns. The 2026 sixth-round swap is the canonical case, and every earlier pass turned
 *    it into a pick.
 *
 * 3. RECORDING WITHOUT AFFIRMATION IS COMMISSIONER-ONLY, and it is a stated choice — a checkbox
 *    reading "both GMs have already agreed" — rather than a side effect of filling in a time.
 *    A GM filing here supplies no timestamp because filing IS the declaration and the clock
 *    starts when the other man affirms; giving him the field would let him back-date his own
 *    window. The record says the commissioner entered it for both, never that the other side
 *    affirmed something he did not.
 *
 * 5. THE KEEPERS ARE PART OF THE PRICE. A pick with a keeper charged to it is offered with his
 *    name on it and stays selectable — the deal may be worth it — and the form says what it
 *    costs before it is filed: who moves to an earlier round, or that the slate no longer fits
 *    at all, in which case it refuses. What the form warns about, `Trades.settle` carries out
 *    when the window closes. See `domain/tradeKeepers.ts`.
 *
 * 4. THE PARITY LINE IS A WARNING AND NOTHING ELSE. It appears when same-season picks do not
 *    balance and is absent otherwise — no green tick. A reassurance on every trade that moves no
 *    picks trains the eye to skip the line, and this is the line that must be read.
 */

import { useEffect, useMemo, useState } from 'react';
import { LEAGUE_2026 } from '../domain/config.js';
import { gmOfTeam, managerShort } from '../domain/identity.js';
import { holdingsFor, ownedRoundsFor } from '../domain/picks2026.js';
import {
  announcementCheck, parityWarning, pickCoords, pickParity, windowPreview,
  type LiveSide, type TradeAsset, type WindowCtx,
} from '../domain/tradeWindow.js';
import {
  chargedNow, farmCapCheck, keeperEffect, keepersAfterTrade, keepersOnPicks, keepersSent,
  movesSay, pricedSlate, type PricedRow,
} from '../domain/tradeKeepers.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import { faab } from '../domain/faab.js';
import { shortfallSays } from '../domain/keeperSlots.js';
import keeperRows from '../data/keepers-2026.json';
import farmLog from '../data/farm-log.json';
import {
  controlledAssets, farmCount, tradePhase, type Controlled, type FarmRow, type RosterRow,
} from '../domain/tradeRoster.js';
import { GMS } from '../store/session.js';
import { AssetStack } from './TradeAssetEditor.js';
import { bareName } from './TradeAssets.js';
import { GmBadge } from './GmBadge.js';
import { Rule } from './RuleText.js';
import { useOwnership } from './useOwnership.js';
import type {
  KeeperDeclaration, LeagueStore, LiveTradeAsset, LiveTradeDoc, Role,
} from '../store/types.js';

const ROWS = keeperRows as unknown as PricedRow[];
const ROSTER = keeperRows as unknown as RosterRow[];
const FARM = farmLog as unknown as FarmRow[];

const SEASON = 2026;

/** Enough of a trade for the rules to be asked about it before it exists. */
const blank = (): Omit<LiveTradeDoc, 'sides' | 'season'> => ({
  id: '', filedBy: '', filedAt: '', counters: [], vetoes: [], clauses: [],
});

interface Props {
  gm: string;
  role: Role;
  /** Where the season is, and — during a draft — where the board is. See `domain/tradeWindow.ts`. */
  ctx: WindowCtx;
  store: LeagueStore;
  /** When slates lock. Before it a keeper-eligible man is dealable as a potential keeper. */
  keeperDeadline?: string | null;
  onFile: (t: LiveTradeDoc) => void;
}

export function TradeEntry({ gm, role, ctx, store, keeperDeadline = null, onFile }: Props) {
  const isCommish = role === 'commissioner' || role === 'admin';
  const me = gm.toUpperCase();
  const [left, setLeft] = useState<string>(me);
  const [right, setRight] = useState<string>('');
  const [mine, setMine] = useState<TradeAsset[]>([]);
  const [theirs, setTheirs] = useState<TradeAsset[]>([]);
  const [agreedAt, setAgreedAt] = useState('');
  /*
   * RECORD IT NOW — the commissioner's option to process a deal without the other GM affirming.
   *
   * It existed already, as a side effect of typing into the "Agreed at" box, and on an iPhone an
   * empty `datetime-local` renders as almost nothing: Tim's screenshot of 1 September shows the
   * label, a gap, and the caption under it, with no visible control between them. An option you
   * can only find by knowing it is there is not an option. So it is a checkbox with a sentence,
   * and the time defaults to now, editable, rather than being the thing you have to fill in to
   * discover the button changes.
   */
  const [recordNow, setRecordNow] = useState(false);
  const [open, setOpen] = useState(false);

  // A GM files as himself; only a commissioner may name the left-hand side, and only because
  // he is recording a conversation he was not in.
  const from = isCommish ? left : me;

  /* The ledger plus every trade settled since — not the build-time file alone. */
  const { ownership, version: ownershipVersion } = useOwnership(store);
  const others = GMS.filter((g) => g.toUpperCase() !== from.toUpperCase());

  /**
   * SUBMITTED SLATES, for two things the form could not say before: which keeper is standing on
   * a pick, and what happens to him if it goes. Both are public facts once a slate is submitted.
   */
  const [decls, setDecls] = useState<KeeperDeclaration[]>([]);
  useEffect(() => {
    let live = true;
    const load = () => store.declarations(SEASON).then((d) => {
      if (live) setDecls(d.filter((x) => x.status === 'submitted'));
    });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'declarations') void load(); });
    return () => { live = false; off(); };
  }, [store]);
  const order = ctx.clock?.order ?? [];
  const phase = tradePhase(Boolean(ctx.inSeason), keeperDeadline);

  /** Everything the keeper questions need about one GM, derived once per side. */
  const slateOf = (who: string) => {
    if (!who || who === '—') return null;
    const decl = decls.find((d) => d.gm.toUpperCase() === who.toUpperCase());
    const keepers = pricedSlate(who, decl, ROWS, gmOfTeam);
    const ownedRounds = ownedRoundsFor(who, ownership, LEAGUE_2026.draftRounds);
    const now = chargedNow(keepers, decl, ownedRounds);
    return {
      decl, keepers, ownedRounds, now,
      onPicks: keepersOnPicks(who, decl, now),
      /* What he can actually put in the deal, for where the season is — `domain/tradeRoster.ts`. */
      own: controlledAssets(who, ROSTER, FARM, decl, gmOfTeam, phase),
    };
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const leftSlate = useMemo(() => slateOf(from), [from, decls, ownershipVersion, phase]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rightSlate = useMemo(() => slateOf(right), [right, decls, ownershipVersion, phase]);

  /** The rounds a side's pick assets move, as a multiset. */
  const roundsOf = (assets: readonly TradeAsset[]) => assets
    .filter((a) => a.kind === 'pick' && a.text.includes(String(SEASON)))
    .map((a) => pickCoords(a.text)?.round)
    .filter((r): r is number => r !== undefined);

  /**
   * WHAT THE DEAL DOES TO EACH MAN'S KEEPERS, worked out as it is typed.
   *
   * Two sentences, and they are different. A slate that no longer fits is a REFUSAL naming the
   * round he has to keep back (Hall's condition, reported as the binding constraint). A slate that
   * still fits but moves keepers earlier is a WARNING naming who moves and where — the cost of
   * most pick trades is that, not the picks, and it is invisible unless the app says it.
   */
  const effects = useMemo(() => {
    const out = [];
    const gives = [roundsOf(mine), roundsOf(theirs)];
    const sends = [mine, theirs];
    const sides = [leftSlate, rightSlate];
    for (const i of [0, 1] as const) {
      const side = sides[i];
      const other = sides[1 - i];
      if (!side) continue;
      /*
       * KEEPERS CHANGE HANDS WITH THEIR STANDING. A declared keeper sent in the deal leaves this
       * slate; one received joins it at the round he already costs (2013-III-3, 2018-III-2), and
       * after the deadline neither GM may un-keep him. So the slate the picks are fitted to is
       * the slate AFTER the players move, and a man arriving shows as "joins at Round N".
       */
      const outgoing = keepersSent(sends[i]!, side.keepers).map((k) => k.player);
      const incoming = other ? keepersSent(sends[1 - i]!, other.keepers) : [];
      const keepers = keepersAfterTrade(side.keepers, outgoing, incoming);
      if (keepers.length === 0) continue;
      const who = i === 0 ? from : right;
      out.push(keeperEffect(who, keepers, side.now, side.ownedRounds, gives[i]!, gives[1 - i]!));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, theirs, leftSlate, rightSlate, from, right]);
  const refused = effects.filter((e) => e.shortfall);
  const shifting = effects.filter((e) => !e.shortfall && e.moves.length > 0);

  /**
   * THE CAP, AND THE TAX. A keeper arriving is a slot used (2023 §1, eight; a franchise tag is
   * three), and the receiver may already be at eight, in which case there is no legal slate for
   * him to arrive into and the trade is refused. Under the cap he still costs FAAB past four.
   * `assessLuxuryTax` is the one implementation of both, so it is asked rather than re-derived.
   */
  const capChecks = useMemo(() => {
    const out: Array<{ gm: string; errors: string[]; taxNow: number; taxAfter: number }> = [];
    const sends = [mine, theirs];
    const sides = [leftSlate, rightSlate];
    for (const i of [0, 1] as const) {
      const side = sides[i]; const other = sides[1 - i];
      if (!side?.decl || !other) continue;
      const incoming = keepersSent(sends[1 - i]!, other.keepers);
      if (incoming.length === 0) continue;
      const outgoing = new Set(keepersSent(sends[i]!, side.keepers).map((k) => k.player));
      const who = (i === 0 ? from : right).toUpperCase();
      const declared = side.decl.keepers
        .filter((k) => !outgoing.has(k.player))
        .map((k) => ({ playerId: k.player, franchiseTagged: k.franchiseTagged, ...(k.farmForfeited ? { farmForfeited: true } : {}) }));
      const tagged = new Set(other.decl?.keepers.filter((k) => k.franchiseTagged).map((k) => k.player) ?? []);
      for (const k of incoming) declared.push({ playerId: k.player, franchiseTagged: tagged.has(k.player) });
      const before = assessLuxuryTax({ teamId: who, declared: side.decl.keepers.map((k) => ({ playerId: k.player, franchiseTagged: k.franchiseTagged })) }, LEAGUE_2026);
      const after = assessLuxuryTax({ teamId: who, declared }, LEAGUE_2026);
      out.push({ gm: who, errors: after.errors, taxNow: before.taxFaab, taxAfter: after.taxFaab });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine, theirs, leftSlate, rightSlate, from, right]);
  const overCap = capChecks.filter((c) => c.errors.length > 0);
  const taxed = capChecks.filter((c) => c.errors.length === 0 && c.taxAfter !== c.taxNow);

  /** Farm rights need a farm slot to land on (2022 §1). Refused past the cap, not warned. */
  const farmChecks = useMemo(() => {
    const rights = (assets: readonly TradeAsset[]) => assets.filter((a) => a.kind === 'rights').length;
    const sends = [mine, theirs];
    const gms = [from, right];
    const out = [];
    for (const i of [0, 1] as const) {
      const gm = gms[i]!;
      if (!gm || gm === '—') continue;
      const c = farmCapCheck(managerShort(gm), farmCount(gm, FARM), rights(sends[1 - i]!), rights(sends[i]!), LEAGUE_2026.maxFarmPlayers);
      if (!c.ok) out.push(c);
    }
    return out;
  }, [mine, theirs, from, right]);

  const sides: [LiveSide, LiveSide] = [
    { gm: from, sends: mine },
    { gm: right || '—', sends: theirs },
  ];
  const parity = useMemo(() => pickParity(sides), [mine, theirs, from, right]);
  const ready = right !== '' && (mine.length > 0 || theirs.length > 0)
    && refused.length === 0 && overCap.length === 0 && farmChecks.length === 0;

  /** "2026-09-01T19:50", the local instant, for the datetime field's default. */
  const nowLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const recording = isCommish && recordNow;

  const file = () => {
    if (!ready) return;
    const clauses = [...mine, ...theirs].filter((a) => a.kind === 'clause').map((a) => a.text);
    onFile({
      id: `lt-${Date.now().toString(36)}`,
      season: SEASON,
      sides: [
        { gm: from, sends: mine as LiveTradeAsset[] },
        { gm: right, sends: theirs as LiveTradeAsset[] },
      ],
      filedBy: me,
      filedAt: new Date().toISOString(),
      /*
       * Only a commissioner can supply one, and supplying it starts the clock immediately —
       * there is nobody left to affirm a deal that was already declared elsewhere. It is
       * recorded as HIS act, with his name on it: `agreedSource: 'commissioner'` and `agreedBy`
       * the commissioner, never a fabricated affirmation from the other side. The card reads
       * "recorded by Tim for John", which is what happened.
       */
      ...(recording
        ? {
          agreedAt: new Date(agreedAt || nowLocal()).toISOString(),
          agreedSource: 'commissioner' as const,
          agreedBy: me,
        }
        : {}),
      counters: [], vetoes: [], clauses,
    });
    setMine([]); setTheirs([]); setRight(''); setAgreedAt(''); setRecordNow(false); setOpen(false);
  };

  const otherName = right ? managerShort(right) : 'him';

  /*
   * Checked live as the form is filled, because the answer changes with the assets AND with the
   * board underneath it: adding a 2nd-rounder to a deal can make a legal announcement illegal
   * without the filer touching anything else.
   */
  const announce = ctx.clock
    ? announcementCheck(
      {
        ...blank(),
        season: SEASON,
        sides: [{ gm: from, sends: mine }, { gm: right || '—', sends: theirs }],
      },
      SEASON, ctx,
    )
    : null;

  return (
    <section className={`card trentry ${isCommish ? 'gated' : ''} ${open ? 'open' : ''}`}>
      <div className="trentryhead">
        <button type="button" className="trentrytitle" onClick={() => setOpen(!open)}
          aria-expanded={open}>
          <span className="trplus" aria-hidden="true">{open ? '−' : '+'}</span>
          File a trade
        </button>
        <span className="mut trrequires">
          {recording ? 'recorded on both GMs’ behalf' : 'requires the other GM to affirm'}
        </span>
        {isCommish && <span className="chip commish tiny">Commissioner — on anyone’s behalf</span>}
        <span className="spacer" />
      </div>
      {isCommish && open && (
        <div className="tragreed">
          <label className="trrecord">
            <input type="checkbox" checked={recordNow}
              onChange={(e) => { setRecordNow(e.target.checked); if (e.target.checked && !agreedAt) setAgreedAt(nowLocal()); }} />
            <span>
              <b>Both GMs have already agreed — record it now.</b>
              <span className="mut"> No affirmation is asked for; the window runs from the time
                below and the log says you recorded it for both.</span>
            </span>
          </label>
          {recordNow && (
            <label className="tragreedat">
              <span className="mut">Agreed at</span>
              <input type="datetime-local" value={agreedAt}
                onChange={(e) => setAgreedAt(e.target.value)} />
              <span className="mut trwhy">as declared outside the app</span>
            </label>
          )}
        </div>
      )}

      {open && (
        <>
          <div className="trcols">
            <Column
              who={from} lockedToMe={!isCommish && from === me}
              choices={isCommish ? GMS : []} onWho={setLeft}
              assets={mine} onAssets={setMine} ownership={ownership}
              order={order} keeperOn={leftSlate?.onPicks} own={leftSlate?.own} />
            <span className="trswap" aria-hidden="true">⇄</span>
            <Column
              who={right} lockedToMe={false} choices={others} onWho={setRight}
              assets={theirs} onAssets={setTheirs} ownership={ownership}
              order={order} keeperOn={rightSlate?.onPicks} own={rightSlate?.own} />
          </div>

          {/*
            THE KEEPERS, BEFORE THE PICKS. A pick with a keeper standing on it is selectable — the
            deal may well be worth it — but the form has to say what it costs, and the cost is a
            man moving to an earlier round or a slate that no longer fits at all. Refusal names the
            binding round, not whichever keeper the loop reached last (see `keeperSlots`).
          */}
          {refused.map((e) => (
            <p className="trparity bad" key={`ref-${e.gm}`}>
              <span className="ico" aria-hidden="true">✕</span>
              {managerShort(e.gm)}’s keepers no longer fit: {shortfallSays(e.shortfall)}
              {' '}<Rule id="2015-III-7" />
            </p>
          ))}
          {farmChecks.map((c) => (
            <p className="trparity bad" key={`farm-${c.why}`}>
              <span className="ico" aria-hidden="true">✕</span>
              {c.why} <Rule id="2022-farm-auction-declarations" />
            </p>
          ))}
          {overCap.map((c) => (
            <p className="trparity bad" key={`cap-${c.gm}`}>
              <span className="ico" aria-hidden="true">✕</span>
              {managerShort(c.gm)} cannot take on a declared keeper: {c.errors[0]}
              {' '}<Rule id="2023-keeper-expansion-eight" />
            </p>
          ))}
          {taxed.map((c) => (
            <p className="trparity" key={`tax-${c.gm}`}>
              <span className="ico" aria-hidden="true">!</span>
              {managerShort(c.gm)}’s luxury tax goes from {faab(c.taxNow)} to {faab(c.taxAfter)} with
              the keeper{c.taxAfter > c.taxNow ? ' he takes on' : ' he sends'}.
              {' '}<Rule id="2018-III-5" />
            </p>
          ))}
          {shifting.map((e) => (
            <p className="trparity" key={`mv-${e.gm}`}>
              <span className="ico" aria-hidden="true">!</span>
              {managerShort(e.gm)}’s keepers move: {movesSay(e.moves, bareName)}. Recorded on his
              slate when the trade settles; he can move the hit onto a different keeper on Declare.
              {' '}<Rule id="2015-III-7" />
            </p>
          ))}

          {parity.map((p) => (
            <p className="trparity" key={p.season}>
              <span className="ico" aria-hidden="true">!</span>
              {parityWarning(p, managerShort)} <Rule id="2022-minimum-draft-picks" />
            </p>
          ))}

          {/*
            THE ANNOUNCEMENT DEADLINE, and only during a draft. 2017-II-3 §2 is the rule nobody can
            apply in their head at the time it matters: it asks whether a full round has yet to pass
            before the earliest pick in the deal, on a board that is moving. It is a warning rather
            than a block — a late announcement is the league's call, not the app's, and refusing to
            record one would just move it back into the channel where nothing is checked at all.
          */}
          {announce && !announce.ok && (
            <p className="trparity" key="announce">
              <span className="ico" aria-hidden="true">!</span>
              {announce.why} <Rule id="2017-II-3" />
            </p>
          )}

          <div className="trentryfoot">
            <span className="spacer" />
            <button type="button" className="btn primary" disabled={!ready} onClick={file}>
              {recording ? 'Record the trade' : `Send to ${otherName} to affirm`}
            </button>
          </div>

          <div className="trwindow">
            <div className="trwintop">
              <span className="trwinlab">{recording ? 'From the agreed time' : `Once ${otherName} affirms`}</span>
              <span className="trwintext">
                {/*
                  During a draft the preview stops being about hours. "No counter window" was true
                  and useless; what a man filing at pick 24 needs to know is which pick closes it
                  and whether he is already too late.
                */}
                {announce?.first
                  ? announce.why
                  : windowPreview(agreedAt || Date.now(), ctx)}
              </span>
              <Rule id="2018-II-2" />
            </div>
            {/*
              The three shapes side by side, permanently. A single line saying which one applies
              today answers "how long have I got" and leaves "why 48 and not 24" unanswered, and
              that second question is the one that gets asked in the channel every August.
            */}
            <div className="trwinref">
              <span><b>In season</b> 24 hours — counters for 20, then 4 for the other GM to match</span>
              <span><b>Offseason</b> 48 hours, counters the whole way, up to 48 before the draft</span>
              {/*
                THE LAST BAND IS NOT ONLY DRAFT DAY (Tim, 8 September). 2018-II-2 §2 hands the
                last 48 hours before the first pick, and the draft itself, to 2017-II-3 — which
                is why the offseason line above stops where it does. Calling the band "Draft day"
                left the two lines describing the same evening.
              */}
              <span><b>The last 48 hours, and the draft</b> measured in picks, not hours —
                announced a full round, ten picks, before the first pick in it, and counters run
                until that pick is made</span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/* -- one side ---------------------------------------------------------------------------------- */

function Column(
  { who, lockedToMe, choices, onWho, assets, onAssets, ownership, order, keeperOn, own }: {
    who: string;
    lockedToMe: boolean;
    choices: readonly string[];
    onWho: (g: string) => void;
    assets: TradeAsset[];
    onAssets: (a: TradeAsset[]) => void;
    ownership: ReadonlyMap<string, string>;
    order: readonly string[];
    keeperOn?: ReadonlyMap<string, string>;
    own?: readonly Controlled[];
  },
) {
  const holdings = useMemo(
    () => (who && who !== '—' ? holdingsFor(who, ownership, LEAGUE_2026.draftRounds) : []),
    [who, ownership],
  );
  return (
    <div className="trcol">
      {lockedToMe ? (
        <div className="trwho me">
          <GmBadge gm={who} withName />
          <span className="mut tag">You</span>
        </div>
      ) : (
        <label className="trwho">
          {who && who !== '—' ? <GmBadge gm={who} /> : <span className="gmdot blank">—</span>}
          <select value={who === '—' ? '' : who} onChange={(e) => onWho(e.target.value)}>
            <option value="">Which GM</option>
            {choices.map((g) => <option key={g} value={g}>{managerShort(g)}</option>)}
          </select>
        </label>
      )}

      <AssetStack assets={assets} onAssets={onAssets} holdings={holdings} who={who}
        order={order} keeperOn={keeperOn} own={own} disabled={!who || who === '—'} />
    </div>
  );
}
